import { Link } from "react-router-dom";
import { ArrowUpRight, KeyRound, Bot, Repeat, ShieldCheck } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { PageHero, CTABand } from "../components/PageParts";
import SectionHeading from "../components/SectionHeading";
import ProcessFlow from "../components/ProcessFlow";
import RiskNote from "../components/RiskNote";
import { FAQ, IllustrativeChartCard } from "../components/Cards";
import { FAQS, EXTERNAL_LINKS } from "../config/site";

const STEPS = [
  {
    title: "Create a trade-only API key",
    desc: "On your own exchange account, create an API key with trade permission — never withdrawal permission.",
  },
  {
    title: "Connect the strategy",
    desc: "The strategy engine sends order instructions through the API into your exchange account.",
  },
  {
    title: "Orders execute in your account",
    desc: "Fills settle in your own custody. The strategy never touches a pooled fund or our balance sheet.",
  },
  {
    title: "Monitor and disconnect anytime",
    desc: "Track positions in your exchange app and revoke the API key at any moment to stop execution.",
  },
];

const STRATEGIES = [
  {
    id: "strat-01",
    name: "Kalki Trend Core",
    objective: "Capture sustained directional moves in major crypto pairs.",
    methodology: "Trend-following on multiple timeframes with volatility-adjusted position sizing and systematic stop logic.",
    exchanges: "Binance · Bybit",
    backtest: "Backtested — historical simulation",
    drawdown: "Published with verified data",
  },
  {
    id: "strat-02",
    name: "Kalki Range Rotator",
    objective: "Harvest range-bound behaviour across liquid altcoin pairs.",
    methodology: "Mean-reversion entries inside statistically defined ranges, with regime filters that stand aside during trending regimes.",
    exchanges: "OKX",
    backtest: "Backtested — historical simulation",
    drawdown: "Published with verified data",
  },
  {
    id: "strat-03",
    name: "Kalki Basis Guard",
    objective: "Seek carry with reduced directional exposure, hedged.",
    methodology: "Cash-and-carry construction across spot and perpetual futures, monitored continuously for funding and basis risk.",
    exchanges: "Binance",
    backtest: "Backtested — historical simulation",
    drawdown: "Published with verified data",
  },
];

const API_POINTS = [
  {
    icon: KeyRound,
    title: "Trade permission only",
    desc: "Setup instructions always specify trade-only keys. Withdrawal permission is never requested or used.",
  },
  {
    icon: Bot,
    title: "Signals, not promises",
    desc: "The engine dispatches systematic signals. It cannot move funds off the exchange, only place and manage orders.",
  },
  {
    icon: Repeat,
    title: "Execution on your venue",
    desc: "Orders execute on your exchange, at your fee tier, inside your own account limits.",
  },
  {
    icon: ShieldCheck,
    title: "Revocable in one click",
    desc: "You can delete the API key from your exchange settings at any time; execution stops immediately.",
  },
];

export default function StrategyTrading() {
  return (
    <>
      <Seo
        title="Strategy Trading — API-Connected & Non-Custodial"
        path="/strategy-trading"
        description="Algorithmic strategies that operate through your own exchange account via supported API connectivity. Objectives, methodology, backtesting context and risk explained."
      />

      <PageHero
        eyebrow="Offering 02 · Strategy Trading"
        titleLines={["Algorithmic strategies,", "executed in your account."]}
        intro="Showcase of algorithmic trading strategies designed to operate through users' own exchange accounts using supported API connectivity — a non-custodial structure where you keep custody and can disconnect at any time."
        badges={["Non-custodial", "Native API integration"]}
        image="https://images.unsplash.com/photo-1513346940221-6f673d962e97?crop=entropy&cs=srgb&fm=jpg&q=85"
        imageAlt="Streams of golden data lines across a black background"
      />

      {/* Overview */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-14">
            <SectionHeading
              eyebrow="API-Based Execution"
              title={<>Your keys, your venue, your custody</>}
              intro="This is an informational marketing page: it explains how API-based execution works. It does not connect accounts, execute trades or manage anything on your behalf."
            />
            <Reveal delay={0.15} className="flex flex-col justify-center gap-5">
              <p className="text-sm md:text-base text-zinc-400 leading-relaxed">
                Each strategy defines an objective, a trading methodology and the exchanges it
                supports. Where backtesting exists, it is labelled as backtested and its inherent
                limitations are stated alongside it.
              </p>
              <p className="text-sm md:text-base text-zinc-400 leading-relaxed">
                Execution happens entirely on your exchange account. We never request withdrawal
                permissions, hold client keys in operating systems, or pool participant capital.
              </p>
              <div className="glass-gold rounded-2xl p-5 mt-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-400/80 mb-2">
                  Custody arrangement
                </p>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  Non-custodial. Assets never leave your exchange account; the strategy interacts
                  only through a trade-only API key you can revoke at any time.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="mt-20">
            <ProcessFlow idPrefix="strategy" steps={STEPS} />
          </div>
        </div>
      </section>

      {/* Strategy cards */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10 bg-ink-800/40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="Strategy Showcase"
            title={<>Objectives, methodology, labelled results.</>}
            intro="Risk metrics and drawdown figures are published only when verified. Backtested figures are labelled as such and carry their limitations."
          />
          <Stagger className="mt-14 grid gap-6 lg:grid-cols-3">
            {STRATEGIES.map((s) => (
              <StaggerItem key={s.id} className="h-full">
                <article
                  data-testid={`strategy-card-${s.id}`}
                  className="glass rounded-3xl p-7 h-full flex flex-col hover:border-gold-400/35 transition-colors duration-500"
                >
                  <span className="px-3 py-1 rounded-full font-mono text-[9px] uppercase tracking-[0.16em] text-amber-200/80 border border-zinc-500/30 bg-zinc-500/10 w-fit mb-5">
                    {s.backtest}
                  </span>
                  <h3 className="font-display text-xl font-bold tracking-tight text-ink-50 mb-3">
                    {s.name}
                  </h3>
                  <div className="flex flex-col gap-4 mb-7">
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-gold-400/70 mb-1">Objective</p>
                      <p className="text-sm text-zinc-300 leading-relaxed">{s.objective}</p>
                    </div>
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-gold-400/70 mb-1">Methodology</p>
                      <p className="text-sm text-zinc-400 leading-relaxed">{s.methodology}</p>
                    </div>
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-gold-400/70 mb-1">Supported exchanges</p>
                      <p className="text-sm text-zinc-300">{s.exchanges}</p>
                    </div>
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-gold-400/70 mb-1">Max drawdown</p>
                      <p className="text-sm text-zinc-500 italic">{s.drawdown}</p>
                    </div>
                  </div>
                  <a
                    href={EXTERNAL_LINKS.strategyTrading}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`strategy-link-${s.id}`}
                    className="mt-auto inline-flex items-center justify-center gap-2 rounded-full border border-gold-400/35 bg-gold-400/[0.05] px-5 py-2.5 text-sm font-semibold text-gold-300 hover:bg-gold-400/[0.1] hover:border-gold-400/60 transition-all duration-300"
                  >
                    Strategy details
                    <ArrowUpRight size={14} />
                  </a>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.1}>
            <p className="mt-8 text-xs text-zinc-600 font-mono">
              External strategy destinations are placeholders, configured in the site's content
              configuration file.
            </p>
          </Reveal>
        </div>
      </section>

      {/* API safety + chart */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading eyebrow="Execution Safety" title={<>Four principles of the API model</>} />
          <Stagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {API_POINTS.map((p) => (
              <StaggerItem key={p.title} className="h-full">
                <div className="glass rounded-2xl p-6 h-full hover:border-gold-400/30 transition-colors duration-500">
                  <p.icon size={20} className="text-gold-400 mb-5" />
                  <h3 className="text-sm font-semibold text-ink-50 mb-2">{p.title}</h3>
                  <p className="text-xs text-zinc-500 leading-relaxed">{p.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <div className="mt-16">
            <IllustrativeChartCard testid="strategy-illustrative-chart" />
          </div>
        </div>
      </section>

      {/* Risk + FAQ */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <RiskNote
            lines={[
              "Strategies execute on your exchange account through API keys. While withdrawal permission is never requested, your account remains exposed to the exchange as counterparty and to each strategy's market risk.",
              "Backtested results are hypothetical simulations with inherent limitations: they cannot fully reproduce fees, slippage, liquidity and execution realities. Past performance never guarantees future results.",
            ]}
          />
          <div className="mt-16">
            <SectionHeading eyebrow="FAQ" title={<>Frequently asked questions</>} />
            <div className="mt-10">
              <FAQ items={FAQS.strategy} idPrefix="strategy-faq" />
            </div>
          </div>
        </div>
      </section>

      <CTABand
        testid="strategy-cta"
        title="Review the strategies in full on the platform."
        subtitle="The configured destination carries the live strategy documentation — or ask the team anything first."
        primary={{ label: "Explore Strategies", to: EXTERNAL_LINKS.strategyTrading, external: true }}
        secondary={{ label: "Contact Our Team", to: "/contact" }}
      />
    </>
  );
}